import { create } from "zustand";
import type { LadderStep } from "../lib/notHeard";

/**
 * "You're not being heard" state for the current call (#2224 D5), written by
 * the runner inside `VoicePathMonitor` and read by the banners (signed-in
 * `CallSessionRoom`, guest `MeetingRoom`) and the tiles.
 *
 * `rejoin` is registered by whichever call page is live: the signed-in call
 * (its reconnect controller) or, since #2223 AC46, the guest page (a client
 * disconnect that useGuestReconnect turns into an automatic rejoin). Both
 * the speaker's not-heard ladder and the listener's can't-hear ladder use it,
 * always through lib/rejoinGate.ts.
 */
interface NotHeardState {
  /** This device's own warning is showing. */
  warning: boolean;
  /** Where the recovery ladder is. */
  step: LadderStep;
  /** Identities whose tile shows "can't be heard right now". */
  marks: string[];
  /** Starts a full rejoin; false when none could be started. */
  rejoin: (() => boolean) | null;
  /** Bumped by the "Reconnect audio" button; the runner reruns the ladder. */
  retryNonce: number;
  setSelf: (warning: boolean, step: LadderStep) => void;
  setMarks: (marks: string[]) => void;
  setRejoin: (rejoin: (() => boolean) | null) => void;
  requestRetry: () => void;
  /** End of call: drop everything but the rejoin registration (owned by its bridge). */
  reset: () => void;
}

export const useNotHeardStore = create<NotHeardState>((set) => ({
  warning: false,
  step: "idle",
  marks: [],
  rejoin: null,
  retryNonce: 0,
  setSelf: (warning, step) => set({ warning, step }),
  setMarks: (marks) =>
    set((s) => (s.marks.length === marks.length && s.marks.every((m, i) => m === marks[i]) ? s : { marks })),
  setRejoin: (rejoin) => set({ rejoin }),
  requestRetry: () => set((s) => ({ retryNonce: s.retryNonce + 1 })),
  reset: () => set({ warning: false, step: "idle", marks: [] }),
}));

/** Whether this participant's tile shows "can't be heard right now". */
export function useNotHeardMark(identity: string | undefined): boolean {
  return useNotHeardStore((s) => !!identity && s.marks.includes(identity));
}
